// ==============================
// Imports
// ==============================

// MUI Components
import AppBarSpacer from '../components/common/AppBarSpacer';
import { Typography, Box, Button, Card, CardActionArea } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  DynamicForm,
  PersonAddAlt1,
  Domain,
  HowToReg,
  EventAvailable,
} from '@mui/icons-material';

// Libraries
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

// Local Components
import MiniDrawer from '../components/MiniDrawer';
import PageHeader from '../components/common/PageHeader';
import QuickActions, { QuickActionHandlers } from '../components/QuickActions';
import QuickCustomerSearch from '../components/QuickCustomerSearch';
import { useTranslation } from '../i18n';

// ==============================
// Component
// ==============================
const HomeLayout = () => {
  // Sidebar state
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Home quick-action cards. `onClick` is resolved from the handlers provided
  // by <QuickActions/> at render time (see below).
  const buildQuickActions = (
    handlers: QuickActionHandlers,
  ): {
    title: string;
    description: string;
    icon: ReactNode;
    tone: 'primary' | 'info' | 'success' | 'warning';
    onClick: () => void;
  }[] => [
    {
      title: t('home.newUser.title'),
      description: t('home.newUser.description'),
      icon: <PersonAddAlt1 sx={{ fontSize: 48 }} />,
      tone: 'primary',
      onClick: handlers.openNewUser,
    },
    {
      title: t('home.newOrg.title'),
      description: t('home.newOrg.description'),
      icon: <Domain sx={{ fontSize: 48 }} />,
      tone: 'info',
      onClick: handlers.openNewOrg,
    },
    {
      title: t('home.existingUser.title'),
      description: t('home.existingUser.description'),
      icon: <HowToReg sx={{ fontSize: 48 }} />,
      tone: 'success',
      onClick: handlers.openExistedUser,
    },
    {
      title: t('home.trialBooking.title'),
      description: t('home.trialBooking.description'),
      icon: <EventAvailable sx={{ fontSize: 48 }} />,
      tone: 'warning',
      onClick: handlers.openTrialBooking,
    },
  ];

  // ==============================
  // Render
  // ==============================
  return (
    <>
      <div className="flex h-screen">
        <MiniDrawer open={open} setOpen={setOpen} />
        <main className="flex-1 overflow-auto app-page-bg">
          <AppBarSpacer />
          <div className="h-full px-6 py-4">
            <PageHeader
              title={t('home.pageTitle')}
              subtitle={t('home.pageSubtitle')}
            />

            {/* Quick actions */}
            <QuickActions>
              {(handlers) => (
                <>
                <QuickCustomerSearch handlers={handlers} />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      lg: 'repeat(4, 1fr)',
                    },
                    gap: 3,
                    mt: 3,
                  }}
                >
                  {buildQuickActions(handlers).map((action) => (
                    <Card
                      key={action.title}
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                        transition:
                          'transform .25s ease, box-shadow .25s ease, border-color .25s ease',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          boxShadow: 8,
                          borderColor: 'transparent',
                        },
                      }}
                    >
                      <CardActionArea
                        onClick={action.onClick}
                        sx={{ height: '100%', p: 4 }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={(theme) => ({
                              width: 96,
                              height: 96,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: alpha(
                                theme.palette[action.tone].main,
                                0.12,
                              ),
                              color: theme.palette[action.tone].main,
                            })}
                          >
                            {action.icon}
                          </Box>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color="secondary.main"
                          >
                            {action.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {action.description}
                          </Typography>
                        </Box>
                      </CardActionArea>
                    </Card>
                  ))}
                </Box>
                </>
              )}
            </QuickActions>
          </div>
        </main>
      </div>
    </>
  );
};

export default HomeLayout;
